import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  teamApi,
  TeamSummary,
  TeamMember,
  TeamUsage,
  TeamDetail,
} from '@/api/teamApi';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Loader2,
  Plus,
  Users,
  Mail,
  Trash2,
  Crown,
  Shield,
  User as UserIcon,
  AlertCircle,
  CheckCircle2,
  ArrowRightLeft,
  Edit2,
  X,
  BarChart2,
} from 'lucide-react';

const roleLabel = (role: string) => {
  if (role === 'OWNER') return '팀장';
  if (role === 'ADMIN') return '관리자';
  return '멤버';
};

const roleIcon = (role: string) => {
  if (role === 'OWNER') return <Crown className="w-3 h-3 text-yellow-500" />;
  if (role === 'ADMIN') return <Shield className="w-3 h-3 text-blue-500" />;
  return <UserIcon className="w-3 h-3 text-gray-400" />;
};

const canManageTeam = (myRole: string, userRole: string) =>
  myRole === 'OWNER' || userRole === 'SUPER_ADMIN';

const canInvite = (myRole: string, userRole: string) =>
  myRole === 'OWNER' || myRole === 'ADMIN' || userRole === 'SUPER_ADMIN';

const canRemoveMember = (myRole: string, targetRole: string, userRole: string) => {
  if (userRole === 'SUPER_ADMIN') return true;
  if (myRole === 'OWNER') return true;
  if (myRole === 'ADMIN' && targetRole === 'MEMBER') return true;
  return false;
};

const canChangeRole = (myRole: string, userRole: string) =>
  myRole === 'OWNER' || userRole === 'SUPER_ADMIN';

const canViewAllUsage = (myRole: string, userRole: string) =>
  myRole === 'OWNER' || myRole === 'ADMIN' || userRole === 'SUPER_ADMIN';

export const TeamManagement: React.FC = () => {
  const { user, myTeams, activeTeamId, setActiveTeamId, setMyTeams } = useAuth();
  const userRole = user?.userRole ?? 'CLIENT';

  // Selected team state
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(
    activeTeamId ?? (myTeams.length > 0 ? myTeams[0].teamId : null)
  );
  const selectedTeam = myTeams.find((t) => t.teamId === selectedTeamId) ?? null;
  const myRole = selectedTeam?.myRole ?? 'MEMBER';
  const ownedTeamsCount = myTeams.filter((t) => t.myRole === 'OWNER').length;
  const canCreateTeam = userRole === 'SUPER_ADMIN' || ownedTeamsCount < 2;

  // Data states
  const [members, setMembers] = useState<TeamMember[]>([]);
  const membersRef = React.useRef<TeamMember[]>([]);
  const [usage, setUsage] = useState<TeamUsage | null>(null);
  const [teamDetail, setTeamDetail] = useState<TeamDetail | null>(null);

  const [loadingMembers, setLoadingMembers] = useState(false);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Create team dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');

  // Edit team name
  const [editingTeamName, setEditingTeamName] = useState(false);
  const [editTeamNameValue, setEditTeamNameValue] = useState('');

  // Invite form
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'ADMIN' | 'MEMBER'>('MEMBER');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteResult, setInviteResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Transfer owner dialog
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [transferTargetUserId, setTransferTargetUserId] = useState('');

  // Delete team confirm
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  // Load members for selected team
  const loadMembers = useCallback(async (teamId: number): Promise<TeamMember[]> => {
    setLoadingMembers(true);
    try {
      const res = await teamApi.getMembers(teamId);
      if (res.data.success) {
        membersRef.current = res.data.data;
        setMembers(res.data.data);
        return res.data.data;
      }
      membersRef.current = [];
      return [];
    } catch {
      membersRef.current = [];
      setMembers([]);
      return [];
    } finally {
      setLoadingMembers(false);
    }
  }, []);

  // Load usage for selected team (API에서 멤버별 사용량 포함)
  const loadUsage = useCallback(async (teamId: number) => {
    setLoadingUsage(true);
    try {
      const res = await teamApi.getUsage(teamId);
      if (!res.data.success) return;
      setUsage(res.data.data);
    } catch {
      setUsage(null);
    } finally {
      setLoadingUsage(false);
    }
  }, []);

  // Reload my teams list
  const reloadMyTeams = useCallback(async () => {
    try {
      const loader = userRole === 'SUPER_ADMIN'
        ? teamApi.getAdminTeams()
        : teamApi.getMyTeams();
      const res = await loader;
      if (res.data.success) {
        const raw = res.data.data;
        const teams = Array.isArray(raw) ? raw : (raw as any)?.content ?? [];
        setMyTeams(teams);
      }
    } catch {
      /* ignore */
    }
  }, [setMyTeams, userRole]);

  useEffect(() => {
    if (!selectedTeamId) {
      setMembers([]);
      setUsage(null);
      return;
    }
    const init = async () => {
      await loadMembers(selectedTeamId);
      await loadUsage(selectedTeamId);
    };
    init();
  }, [selectedTeamId, loadMembers, loadUsage]);

  // — Create team —
  const handleCreateTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();
    if (!newTeamName.trim()) return;
    setActionLoading(true);
    try {
      const res = await teamApi.createTeam(newTeamName.trim());
      if (res.data.success) {
        await reloadMyTeams();
        setSelectedTeamId(res.data.data.teamId);
        setActiveTeamId(res.data.data.teamId);
        setNewTeamName('');
        setShowCreateDialog(false);
        setSuccess('팀이 생성되었습니다.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || '팀 생성에 실패했습니다.');
    } finally {
      setActionLoading(false);
    }
  };

  // — Update team name —
  const handleUpdateTeamName = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();
    if (!selectedTeamId || !editTeamNameValue.trim()) return;
    setActionLoading(true);
    try {
      const res = await teamApi.updateTeam(selectedTeamId, editTeamNameValue.trim());
      if (res.data.success) {
        await reloadMyTeams();
        setEditingTeamName(false);
        setSuccess('팀 이름이 변경되었습니다.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || '팀 이름 변경에 실패했습니다.');
    } finally {
      setActionLoading(false);
    }
  };

  // — Delete team —
  const handleDeleteTeam = async () => {
    if (!selectedTeamId) return;
    clearMessages();
    setActionLoading(true);
    try {
      await teamApi.deleteTeam(selectedTeamId);
      await reloadMyTeams();
      setSelectedTeamId(null);
      setActiveTeamId(null);
      setShowDeleteConfirm(false);
      setSuccess('팀이 삭제되었습니다.');
    } catch (err: any) {
      setError(err.response?.data?.message || '팀 삭제에 실패했습니다.');
      setShowDeleteConfirm(false);
    } finally {
      setActionLoading(false);
    }
  };

  // — Send invite —
  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();
    setInviteResult(null);
    if (!selectedTeamId || !inviteEmail.trim()) return;
    setInviteLoading(true);
    try {
      const email = inviteEmail.trim();
      const res = await teamApi.inviteMember(selectedTeamId, email, inviteRole);
      setInviteEmail('');
      const msg = res.data.message ?? `초대 메일이 발송되었습니다.\n${email} 주소로 초대 링크가 전송됩니다.`;
      setInviteResult({ type: 'success', message: msg });
    } catch (err: any) {
      const status = err.response?.status;
      const data = err.response?.data;
      let msg: string;
      if (status === 403) {
        msg = '초대 권한이 없습니다.';
      } else if (status === 404) {
        msg = '팀을 찾을 수 없습니다.';
      } else if (status === 409) {
        msg = data?.message ?? '이미 초대 중이거나 팀 멤버입니다.';
      } else {
        msg = data?.message ?? data?.errors?.[0]?.message ?? '초대 발송에 실패했습니다.';
      }
      setInviteResult({ type: 'error', message: msg });
    } finally {
      setInviteLoading(false);
    }
  };

  // — Change member role —
  const handleChangeRole = async (userId: string, newRole: 'ADMIN' | 'MEMBER') => {
    if (!selectedTeamId) return;
    clearMessages();
    setActionLoading(true);
    try {
      await teamApi.changeMemberRole(selectedTeamId, userId, newRole);
      await loadMembers(selectedTeamId);
      await loadUsage(selectedTeamId);
      setSuccess('역할이 변경되었습니다.');
    } catch (err: any) {
      setError(err.response?.data?.message || '역할 변경에 실패했습니다.');
    } finally {
      setActionLoading(false);
    }
  };

  // — Remove member —
  const handleRemoveMember = async (userId: string) => {
    if (!selectedTeamId) return;
    if (!window.confirm('팀에서 제거하시겠습니까?')) return;
    clearMessages();
    setActionLoading(true);
    try {
      await teamApi.removeMember(selectedTeamId, userId);
      await loadMembers(selectedTeamId);
      setSuccess('멤버가 제거되었습니다.');
    } catch (err: any) {
      setError(err.response?.data?.message || '멤버 제거에 실패했습니다.');
    } finally {
      setActionLoading(false);
    }
  };

  // — Transfer owner —
  const handleTransferOwner = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTeamId || !transferTargetUserId.trim()) return;
    clearMessages();
    setActionLoading(true);
    try {
      await teamApi.transferOwner(selectedTeamId, transferTargetUserId.trim());
      await reloadMyTeams();
      await loadMembers(selectedTeamId);
      await loadUsage(selectedTeamId);
      setShowTransferDialog(false);
      setTransferTargetUserId('');
      setSuccess('팀장 권한이 이전되었습니다.');
    } catch (err: any) {
      setError(err.response?.data?.message || '팀장 이전에 실패했습니다.');
    } finally {
      setActionLoading(false);
    }
  };

  // ─── No teams: show CTA ───
  if (myTeams.length === 0) {
    return (
      <div className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {success && (
          <Alert className="bg-green-50 border-green-200">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">{success}</AlertDescription>
          </Alert>
        )}

        <Card className="text-center py-10">
          <CardContent>
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-6">소속된 팀이 없습니다.</p>
            <Button onClick={() => setShowCreateDialog(true)} disabled={!canCreateTeam}>
              <Plus className="w-4 h-4 mr-2" />
              팀 만들기
            </Button>
            {!canCreateTeam && (
              <p className="text-xs text-gray-400 mt-2">팀은 최대 2개까지 생성할 수 있습니다.</p>
            )}
          </CardContent>
        </Card>

        {showCreateDialog && (
          <CreateTeamDialog
            value={newTeamName}
            onChange={setNewTeamName}
            onSubmit={handleCreateTeam}
            onClose={() => { setShowCreateDialog(false); setNewTeamName(''); }}
            loading={actionLoading}
          />
        )}
      </div>
    );
  }

  // ─── Has teams ───
  const usagePercent = usage
    ? Math.min(100, Math.round((usage.totalUsed / Math.max(usage.monthlyLimit, 1)) * 100))
    : 0;

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">{success}</AlertDescription>
        </Alert>
      )}

      {/* Team Selector */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-2 flex-wrap">
          {myTeams.map((t) => (
            <button
              key={t.teamId}
              onClick={() => setSelectedTeamId(t.teamId)}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                selectedTeamId === t.teamId
                  ? 'bg-primary text-white border-primary'
                  : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
              }`}
            >
              {t.teamName}
              <span className="ml-1 text-xs opacity-70">({roleLabel(t.myRole)})</span>
            </button>
          ))}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowCreateDialog(true)}
          disabled={!canCreateTeam}
          title={!canCreateTeam ? '팀은 최대 2개까지 생성할 수 있습니다.' : undefined}
        >
          <Plus className="w-4 h-4 mr-1" />
          새 팀
        </Button>
      </div>

      {selectedTeam && (
        <>
          {/* Team Info Card */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex items-center gap-3">
                {editingTeamName ? (
                  <form onSubmit={handleUpdateTeamName} className="flex items-center gap-2">
                    <Input
                      value={editTeamNameValue}
                      onChange={(e) => setEditTeamNameValue(e.target.value)}
                      className="h-8 text-sm"
                      autoFocus
                      disabled={actionLoading}
                    />
                    <Button type="submit" size="sm" disabled={actionLoading}>
                      {actionLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : '저장'}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setEditingTeamName(false)}
                      disabled={actionLoading}
                    >
                      취소
                    </Button>
                  </form>
                ) : (
                  <div className="flex items-center gap-2">
                    <CardTitle>{selectedTeam.teamName}</CardTitle>
                    {canManageTeam(myRole, userRole) && (
                      <button
                        onClick={() => {
                          setEditTeamNameValue(selectedTeam.teamName);
                          setEditingTeamName(true);
                        }}
                        className="text-gray-400 hover:text-gray-600"
                        title="팀 이름 변경"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-blue-100 text-blue-700">{selectedTeam.plan ?? 'FREE'}</Badge>
                {canManageTeam(myRole, userRole) && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowTransferDialog(true)}
                      title="팀장 권한 이전"
                    >
                      <ArrowRightLeft className="w-4 h-4 mr-1" />
                      팀장 이전
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50"
                      onClick={() => setShowDeleteConfirm(true)}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      팀 삭제
                    </Button>
                  </>
                )}
              </div>
            </CardHeader>
            <CardContent className="text-sm text-gray-600 flex gap-4">
              <span>멤버 {members.length}명</span>
              <span>월 한도 {selectedTeam.monthlyLimit?.toLocaleString() ?? '-'}건</span>
              <span className="capitalize">{selectedTeam.status ?? 'ACTIVE'}</span>
            </CardContent>
          </Card>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Members Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  팀원 목록
                </CardTitle>
                <CardDescription>팀원 역할 및 상태 관리</CardDescription>
              </CardHeader>
              <CardContent>
                {loadingMembers ? (
                  <div className="flex justify-center py-6">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : members.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">팀원이 없습니다.</p>
                ) : (
                  <div className="space-y-2">
                    {members.map((member) => {
                      const isPendingRemoval = member.status === 'PENDING_REMOVAL';
                      return (
                        <div
                          key={member.userId}
                          className={`flex items-center justify-between p-3 rounded-lg border ${
                            isPendingRemoval ? 'bg-orange-50 border-orange-200' : 'bg-white border-gray-100'
                          }`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            {roleIcon(member.role)}
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{member.name}</p>
                              <p className="text-xs text-gray-500 truncate">{member.email}</p>
                            </div>
                            <Badge
                              className={
                                member.role === 'OWNER'
                                  ? 'bg-yellow-100 text-yellow-700 ml-1'
                                  : member.role === 'ADMIN'
                                  ? 'bg-blue-100 text-blue-700 ml-1'
                                  : 'bg-gray-100 text-gray-600 ml-1'
                              }
                            >
                              {roleLabel(member.role)}
                            </Badge>
                            {isPendingRemoval && (
                              <Badge className="bg-orange-100 text-orange-700 ml-1">
                                제거 예정
                              </Badge>
                            )}
                          </div>
                          {/* Action buttons — disabled for PENDING_REMOVAL */}
                          {member.role !== 'OWNER' && !isPendingRemoval && (
                            <div className="flex items-center gap-1 ml-2 shrink-0">
                              {canChangeRole(myRole, userRole) && (
                                <select
                                  value={member.role}
                                  onChange={(e) =>
                                    handleChangeRole(
                                      member.userId,
                                      e.target.value as 'ADMIN' | 'MEMBER'
                                    )
                                  }
                                  disabled={actionLoading}
                                  className="text-xs border border-gray-200 rounded px-1.5 py-1 bg-white"
                                >
                                  <option value="MEMBER">멤버</option>
                                  <option value="ADMIN">관리자</option>
                                </select>
                              )}
                              {canRemoveMember(myRole, member.role, userRole) && (
                                <button
                                  onClick={() => handleRemoveMember(member.userId)}
                                  disabled={actionLoading}
                                  className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded disabled:opacity-40"
                                  title="팀에서 제거"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          )}
                          {isPendingRemoval && (
                            <span className="ml-2 text-xs text-orange-500 shrink-0">
                              다음달 자동 삭제
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Invite Card */}
            {canInvite(myRole, userRole) && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    팀원 초대
                  </CardTitle>
                  <CardDescription>이메일로 초대 링크를 발송합니다 (유효기간 7일)</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleInvite} className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">이메일</label>
                      <Input
                        type="email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        placeholder="invite@example.com"
                        required
                        disabled={inviteLoading}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">역할</label>
                      <select
                        value={inviteRole}
                        onChange={(e) => setInviteRole(e.target.value as 'ADMIN' | 'MEMBER')}
                        disabled={inviteLoading}
                        className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white"
                      >
                        <option value="MEMBER">멤버</option>
                        {canChangeRole(myRole, userRole) && (
                          <option value="ADMIN">관리자</option>
                        )}
                      </select>
                    </div>
                    <Button type="submit" disabled={inviteLoading || !inviteEmail.trim()} className="w-full">
                      {inviteLoading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />발송 중...</>
                      ) : (
                        <><Mail className="w-4 h-4 mr-2" />초대 메일 발송</>
                      )}
                    </Button>
                    {inviteResult && (
                      <div
                        className={`flex items-start gap-2 rounded-lg px-3 py-2 text-sm ${
                          inviteResult.type === 'success'
                            ? 'bg-green-50 border border-green-200 text-green-800'
                            : 'bg-red-50 border border-red-200 text-red-700'
                        }`}
                      >
                        {inviteResult.type === 'success'
                          ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-green-600" />
                          : <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-red-500" />}
                        <span className="whitespace-pre-line">{inviteResult.message}</span>
                      </div>
                    )}
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Usage Card */}
            <Card className={canInvite(myRole, userRole) ? 'md:col-span-2' : ''}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart2 className="w-4 h-4" />
                  팀 사용량
                  {usage && (
                    <span className="text-sm font-normal text-gray-500 ml-1">
                      ({usage.month})
                    </span>
                  )}
                </CardTitle>
                <CardDescription>이번 달 팀 ALT 생성 현황</CardDescription>
              </CardHeader>
              <CardContent>
                {loadingUsage ? (
                  <div className="flex justify-center py-6">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : usage ? (
                  <div className="space-y-4">
                    {/* Progress bar */}
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">
                          사용: <span className="font-semibold text-gray-900">{usage.totalUsed.toLocaleString()}</span>건
                        </span>
                        <span className="text-gray-600">
                          한도: <span className="font-semibold">{usage.monthlyLimit.toLocaleString()}</span>건
                        </span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2.5">
                        <div
                          className={`h-2.5 rounded-full transition-all ${
                            usagePercent >= 90
                              ? 'bg-red-500'
                              : usagePercent >= 70
                              ? 'bg-orange-400'
                              : 'bg-primary'
                          }`}
                          style={{ width: `${usagePercent}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1 text-right">
                        잔여 {usage.remaining.toLocaleString()}건 ({100 - usagePercent}%)
                      </p>
                    </div>

                    {/* Member stats - OWNER/ADMIN only */}
                    {canViewAllUsage(myRole, userRole) && usage.memberStats.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">멤버별 사용량</p>
                        <div className="space-y-1">
                          {usage.memberStats.map((stat) => (
                            <div
                              key={stat.userId}
                              className="flex items-center justify-between text-sm"
                            >
                              <div className="flex items-center gap-2">
                                {roleIcon(stat.role)}
                                <span className="text-gray-700">{stat.name}</span>
                                <Badge className="bg-gray-100 text-gray-500 text-xs">
                                  {roleLabel(stat.role)}
                                </Badge>
                              </div>
                              <span className="font-medium">{stat.usedCount.toLocaleString()}건</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">사용량 정보를 불러올 수 없습니다.</p>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* Create Team Dialog */}
      {showCreateDialog && (
        <CreateTeamDialog
          value={newTeamName}
          onChange={setNewTeamName}
          onSubmit={handleCreateTeam}
          onClose={() => { setShowCreateDialog(false); setNewTeamName(''); }}
          loading={actionLoading}
        />
      )}

      {/* Transfer Owner Dialog */}
      {showTransferDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>팀장 권한 이전</CardTitle>
              <CardDescription>
                팀장 권한을 이전하면 본인은 관리자(ADMIN)로 변경됩니다.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleTransferOwner} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">새 팀장 아이디</label>
                  <select
                    value={transferTargetUserId}
                    onChange={(e) => setTransferTargetUserId(e.target.value)}
                    className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white"
                    required
                  >
                    <option value="">-- 선택 --</option>
                    {members
                      .filter((m) => m.role !== 'OWNER')
                      .map((m) => (
                        <option key={m.userId} value={m.userId}>
                          {m.name} ({m.userId}) — {roleLabel(m.role)}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="flex gap-2">
                  <Button type="submit" disabled={actionLoading || !transferTargetUserId} className="flex-1">
                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : '이전'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setShowTransferDialog(false); setTransferTargetUserId(''); }}
                    disabled={actionLoading}
                    className="flex-1"
                  >
                    취소
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete Confirm Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-red-600">팀 삭제</CardTitle>
              <CardDescription>
                <span className="font-semibold">{selectedTeam?.teamName}</span> 팀을 삭제하시겠습니까?
                이 작업은 되돌릴 수 없습니다.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                  onClick={handleDeleteTeam}
                  disabled={actionLoading}
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : '삭제'}
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={actionLoading}
                >
                  취소
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// ── Helper sub-component ──
interface CreateTeamDialogProps {
  value: string;
  onChange: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onClose: () => void;
  loading: boolean;
}
const CreateTeamDialog: React.FC<CreateTeamDialogProps> = ({
  value,
  onChange,
  onSubmit,
  onClose,
  loading,
}) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle>새 팀 만들기</CardTitle>
        <CardDescription>팀 이름을 입력해주세요.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              팀 이름 <span className="text-red-500">*</span>
            </label>
            <Input
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder="예: 개발팀"
              required
              disabled={loading}
              autoFocus
              maxLength={100}
            />
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={loading || !value.trim()} className="flex-1">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : '생성'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading} className="flex-1">
              취소
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  </div>
);
